import { useEffect, useState, useRef } from "react";
import PrintRoot from "./PrintRoot";
import ReportContent from "./ReportContent";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from "recharts";
import { useReactToPrint } from "react-to-print";

import "./SelectiveReadinessOverall.css";

//const BACKEND_URL = "https://web-production-481a5.up.railway.app";
const BACKEND_URL = process.env.REACT_APP_API_URL;
//const BACKEND_URL = "http://localhost:8000";

export default function HomeworkSelectiveReadinessOverall({
  centerCode
}) {
  const [error, setError] = useState(null);

  const [students, setStudents] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState("");
  const [studentSearch, setStudentSearch] = useState("");
  const [showStudentResults, setShowStudentResults] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [overall, setOverall] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const handleSendEmail = async () => {
    if (!selectedStudent || !selectedDate || !printRef.current) return;

    setSendingEmail(true);

    try {
      // install first:
      // npm install html2pdf.js

      const html2pdf = (await import("html2pdf.js")).default;

      const element = printRef.current;

      const pdfBlob = await html2pdf()
        .from(element)
        .set({
          margin: 8,
          filename: "Homework_Selective_Report.pdf",
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: {
            scale: 2,
            useCORS: true
          },
          jsPDF: {
            unit: "mm",
            format: "a4",
            orientation: "portrait"
          }
        })
        .outputPdf("blob");

      const formData = new FormData();

      formData.append("student_id", selectedStudent);
      formData.append("exam_date", selectedDate);

      const pdfFile = new File(
        [pdfBlob],
        `Homework_Selective_Report_${selectedStudent}.pdf`,
        { type: "application/pdf" }
      );

      formData.append("file", pdfFile);

      const res = await fetch(
        `${BACKEND_URL}/api/admin/send-homework-selective-report-email`,
        {
          method: "POST",
          body: formData
        }
      );

      const data = await res.json();

      if (!res.ok) {
        alert(data.message || "Failed to send email");
        return;
      }

      alert("Email sent successfully ✅");
    } catch (error) {
      console.error("SEND EMAIL ERROR:", error);
      alert(error?.message || "Failed to generate/send PDF");
    } finally {
      setSendingEmail(false);
    }
  };

  const printRef = useRef(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: "Homework_Selective_Readiness_Report",
  });

  const MAX_SCORES = {
    reading: 100,
    mathematical_reasoning: 100,
    thinking_skills: 100,
    writing: 25,
  };

  const SUBJECT_LABELS = {
    reading: "Reading",
    mathematical_reasoning: "Mathematical Reasoning",
    thinking_skills: "Thinking Skills",
    writing: "Writing",
  };

  /* ============================
     Helpers
  ============================ */

  const normalizeScore = (subject, value) => {
  const actualValue =
    typeof value === "object" && value !== null
      ? value.score_percent
      : value;

  if (actualValue == null) return 0;

  return Math.round(actualValue);
};

  /* ============================
     Load students
  ============================ */

  useEffect(() => {
    fetch(
      `${BACKEND_URL}/api/admin/students_center_specific?center_code=${centerCode}`
    )
      .then(res => res.json())
      .then(data =>
        setStudents(
          Array.isArray(data)
            ? data
            : []
        )
      );
  }, [centerCode]);

  /* ============================
     Load available homework exam dates
  ============================ */

  useEffect(() => {
    if (!selectedStudent) return;

    setLoading(true);

    fetch(
      `${BACKEND_URL}/api/admin/students/${selectedStudent}/homework-selective-report-dates`
    )
      .then(res => res.json())
      .then(data => {
        setAvailableDates(Array.isArray(data) ? data : []);
        setSelectedDate("");
        setOverall(null);
      })
      .finally(() => setLoading(false));
  }, [selectedStudent]);

  /* ============================
     Generate Overall Homework Report
  ============================ */

  const generateOverallReport = async () => {
    if (!selectedStudent || !selectedDate) return;

    setLoading(true);
    setError(null);
    setOverall(null);

    try {
      const res = await fetch(
        `${BACKEND_URL}/api/admin/students/${selectedStudent}/overall-selective-homework-report?exam_date=${selectedDate}`,
        { method: "POST" }
      );

      const data = await res.json();
      console.log(
        "FULL RESPONSE JSON:",
        JSON.stringify(data, null, 2)
      );

      console.log(
        "READING SCORE OBJECT:",
        JSON.stringify(data.components?.reading, null, 2)
      );

      console.log(
        "NORMALIZED READING SCORE:",
        normalizeScore("reading", data.components?.reading)
      );

      if (!res.ok) {
        // Backend-controlled error handling
        setError(data);
        setShowPreview(false);

        return;
      }

      setOverall(data);
    } catch (err) {
      setError({
        code: "NETWORK_ERROR",
        message: "Unable to reach server. Please try again."
      });
    } finally {
      setLoading(false);
    }
  };

  /* ============================
     Derived visuals
  ============================ */

  const subjectChartData =
    overall && overall.components
      ? Object.entries(overall.components).map(([k, v]) => ({
          subject: SUBJECT_LABELS[k],
          score: normalizeScore(k, v),
        }))
      : [];

  let balanceIndex = null;
  let rawBalance = null;
  let strengths = [];
  let improvements = [];

  if (overall && overall.components) {
    const normalizedScores = Object.entries(overall.components).map(
      ([k, v]) => normalizeScore(k, v)
    );

    rawBalance =
      100 - (Math.max(...normalizedScores) - Math.min(...normalizedScores));

    balanceIndex = Math.max(15, Math.round(rawBalance));

    Object.entries(overall.components).forEach(([k, v]) => {
      const pct = normalizeScore(k, v);

      if (pct >= 90) strengths.push(SUBJECT_LABELS[k]);
      else improvements.push(SUBJECT_LABELS[k]);
    });
  }

  /* ============================
     Subject Focus Card
  ============================ */

  function SubjectFocusCard({ subjectKey, label, rawScore }) {
    const percent = normalizeScore(subjectKey, rawScore);
    console.log("SUBJECT CARD:", {
      subjectKey,
      rawScore,
      percent
    });

    // Extract safely from new backend format
    const obtained =
      rawScore?.obtained ?? null;

    const total =
      rawScore?.total ?? null;

    const isWarning =
      subjectKey === "writing" && percent < 95;

    // Smart display logic
    let displayText = "";

    if (obtained !== null && total !== null) {
      displayText = `${obtained} / ${total} (${percent}%)`;
    } else {
      displayText = `${percent}%`;
    }

    return (
      <div className={`subject-focus ${isWarning ? "warning" : ""}`}>
        <h4>{label} Performance</h4>

        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{
              width: `${percent}%`,
              background: isWarning
                ? "linear-gradient(90deg,#f59e0b,#f97316)"
                : "#2563eb"
            }}
          />
        </div>

        <p>
          {label} Score: {displayText}
        </p>
      </div>
    );
  }

  /* ============================
     UI
  ============================ */

  return (
    <div className="overall-readiness-container">
      <h2 className="overall-title">
        Overall Homework Selective Readiness
      </h2>

      {/* ================= STUDENT SELECTOR ================= */}

      <div className="selector-row">
        <label>Student ID</label>

        <div style={{ width: "100%" }}>
          <input
            type="text"
            placeholder="Search student by ID or name..."
            value={studentSearch}
            onChange={(e) => {
              setStudentSearch(e.target.value);
              setShowStudentResults(true);
            }}
            style={{ width: "100%", boxSizing: "border-box" }}
          />

          {showStudentResults && studentSearch && (
            <div
              role="listbox"
              style={{
                width: "100%",
                boxSizing: "border-box",
                backgroundColor: "#ffffff",
                border: "1px solid #d1d5db"
              }}
            >
              {students
                .filter((student) => {
                  const searchValue = studentSearch.toLowerCase();

                  return (
                    String(student.student_id)
                      .toLowerCase()
                      .includes(searchValue) ||
                    String(student.name).toLowerCase().includes(searchValue)
                  );
                })
                .map((student, index, matchingStudents) => (
                  <div
                    key={student.student_id}
                    role="option"
                    aria-selected="false"
                    onClick={() => {
                      setSelectedStudent(student.student_id);
                      setStudentSearch(
                        `${student.student_id} - ${student.name}`
                      );
                      setShowStudentResults(false);
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#f3f4f6";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#ffffff";
                    }}
                    style={{
                      padding: "8px 10px",
                      backgroundColor: "#ffffff",
                      color: "#1f2937",
                      borderBottom:
                        index < matchingStudents.length - 1
                          ? "1px solid #d1d5db"
                          : "none",
                      textAlign: "left",
                      fontWeight: "normal",
                      cursor: "pointer"
                    }}
                  >
                    {student.student_id} - {student.name}
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* ================= DATE SELECTOR ================= */}

      {selectedStudent && availableDates.length > 0 && (
        <div className="selector-row">
          <label>Homework Attempt Date</label>

          <select
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          >
            <option value="">Select date</option>

            {availableDates.map((date) => (
              <option key={date} value={date}>
                Week starting {date}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* ================= ACTION BUTTONS ================= */}

      {selectedStudent && selectedDate && (
        <div className="action-row">
          <button
            className="generate-button"
            onClick={generateOverallReport}
            disabled={loading}
          >
            {loading
              ? "Generating..."
              : "Generate Overall Homework Readiness Report"}
          </button>

          {overall && (
            <>
              <button
                className="generate-button secondary"
                onClick={() => setShowPreview(true)}
              >
                Preview PDF
              </button>

              <button
                className="generate-button secondary"
                onClick={handleSendEmail}
                disabled={sendingEmail}
              >
                {sendingEmail ? "Sending..." : "Send Email"}
              </button>
            </>
          )}
        </div>
      )}

      {error && (
        <div className="error-box">
          <h4>
            Unable to Generate Homework Report because the student has not
            attempted all four exams
          </h4>

          <p>{error.message}</p>

          {error.code === "INCOMPLETE_EXAMS" && (
            <p>
              Missing exams:{" "}
              <strong>{error.missing_subjects.join(", ")}</strong>
            </p>
          )}

          {error.code === "NO_EXAMS_FOUND" && (
            <p>
              The student has not attempted any homework exams on the selected
              date.
            </p>
          )}
        </div>
      )}

      {/* ================= SCREEN REPORT ================= */}

      {overall && !error && (
        <div className="overall-summary">
          <ReportContent
            overall={overall}
            balanceIndex={balanceIndex}
            strengths={strengths}
            improvements={improvements}
            subjectChartData={subjectChartData}
            SUBJECT_LABELS={SUBJECT_LABELS}
            SubjectFocusCard={SubjectFocusCard}
          />
        </div>
      )}

      {/* ================= PDF PREVIEW MODAL ================= */}

      {showPreview && overall && (
        <div className="pdf-modal-overlay">
          <div className="pdf-modal">

            <div className="pdf-toolbar">
              <button
                onClick={() => {
                  console.group("🖨️ PRINT DEBUG");

                  console.log(
                    "1️⃣ printRef.current:",
                    printRef.current
                  );

                  if (printRef.current) {
                    console.log(
                      "2️⃣ innerHTML length:",
                      printRef.current.innerHTML.length
                    );

                    console.log(
                      "3️⃣ innerHTML preview:",
                      printRef.current.innerHTML.slice(0, 300)
                    );

                    console.log(
                      "4️⃣ childElementCount:",
                      printRef.current.childElementCount
                    );

                    console.log(
                      "5️⃣ offsetHeight:",
                      printRef.current.offsetHeight
                    );
                  }

                  console.groupEnd();

                  handlePrint();
                }}
              >
                Save / Print PDF
              </button>

              <button onClick={() => setShowPreview(false)}>
                Close
              </button>
            </div>

            <div className="pdf-preview-body">
              <PrintRoot
                overall={overall}
                balanceIndex={balanceIndex}
                strengths={strengths}
                improvements={improvements}
                subjectChartData={subjectChartData}
                SUBJECT_LABELS={SUBJECT_LABELS}
              />
            </div>

          </div>
        </div>
      )}

      <div className="print-only">
        <PrintRoot
          ref={printRef}
          overall={overall}
          balanceIndex={balanceIndex}
          strengths={strengths}
          improvements={improvements}
          subjectChartData={subjectChartData}
          SUBJECT_LABELS={SUBJECT_LABELS}
        />
      </div>

    </div>
  );
}